import type { Money } from '@aurora-pvi/contracts';

/**
 * Small shared helpers.
 *
 * Money is the reason this file exists. The contract stores amounts as decimal
 * strings precisely so that no float ever rounds a dollar away, and the seed
 * would defeat that instantly if it summed `43.6 + 11.0` in JavaScript. So the
 * generators keep every amount in whole cents as an integer and only render a
 * decimal string at the boundary — which is also why a product's cost breakdown
 * can be guaranteed to sum exactly to its total.
 */

export const USD = 'USD';

/** Cents → `Money`. The only place a currency amount becomes a string. */
export function money(cents: number, currency: string = USD): Money {
  const rounded = Math.round(cents);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const whole = Math.floor(abs / 100);
  const frac = (abs % 100).toString().padStart(2, '0');
  return { amount: `${sign}${whole}.${frac}`, currency };
}

/** Dollars (integer or float) → cents. Use at the point of authoring a figure. */
export function dollars(amount: number): number {
  return Math.round(amount * 100);
}

/** Millions of dollars → cents. Most airline figures are quoted this way. */
export function millions(amount: number): number {
  return Math.round(amount * 1_000_000 * 100);
}

export function sumCents(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/**
 * Split `total` cents into parts by weight with the remainder pushed onto the
 * largest part, so the parts always sum to exactly the total. Percentages that
 * "nearly" add up are the single most common tell of fabricated data.
 */
export function splitCents(total: number, weights: readonly number[]): number[] {
  const weightTotal = weights.reduce((sum, w) => sum + w, 0);
  if (weightTotal <= 0) return weights.map(() => 0);
  const parts = weights.map((w) => Math.floor((total * w) / weightTotal));
  const remainder = total - parts.reduce((sum, p) => sum + p, 0);
  let largest = 0;
  for (let i = 1; i < parts.length; i += 1) {
    if ((parts[i] ?? 0) > (parts[largest] ?? 0)) largest = i;
  }
  parts[largest] = (parts[largest] ?? 0) + remainder;
  return parts;
}

/** Ratio 0–1, rounded to four places so the UI never prints 0.30000000000000004. */
export function ratio(numerator: number, denominator: number): number {
  if (denominator === 0) return 0;
  return Math.round((numerator / denominator) * 10000) / 10000;
}

export function round(value: number, places = 2): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// ── Dates ────────────────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

/** Parse `YYYY-MM-DD` at UTC midnight. Deterministic: no locale, no clock. */
export function parseDate(isoDate: string): number {
  return Date.parse(`${isoDate}T00:00:00.000Z`);
}

export function toIsoDate(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

export function toTimestamp(epochMs: number): string {
  return new Date(epochMs).toISOString().replace('Z', '+00:00');
}

export function addDays(isoDate: string, days: number): string {
  return toIsoDate(parseDate(isoDate) + days * MS_PER_DAY);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((parseDate(to) - parseDate(from)) / MS_PER_DAY);
}

/** A timestamp `daysAgo` days and `minutes` minutes before the reference date. */
export function timestampBefore(isoDate: string, daysAgo: number, minutes = 0): string {
  return toTimestamp(parseDate(isoDate) - daysAgo * MS_PER_DAY + minutes * 60_000);
}

/** Month labels for the trailing `count` months ending on the month of `isoDate`. */
export function trailingMonths(isoDate: string, count: number): string[] {
  const end = new Date(parseDate(isoDate));
  const labels: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - i, 1));
    labels.push(
      `${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} '${String(
        d.getUTCFullYear(),
      ).slice(2)}`,
    );
  }
  return labels;
}

// ── Index access under `noUncheckedIndexedAccess` ────────────────────────────

/** Indexed read that fails loudly. Generators index a lot; `!` would hide bugs. */
export function at<T>(items: readonly T[], index: number): T {
  const value = items[index];
  if (value === undefined) {
    throw new Error(`at: index ${index} out of range (length ${items.length})`);
  }
  return value;
}

/** Map read that fails loudly, naming the key so a broken reference is findable. */
export function must<T>(map: ReadonlyMap<string, T>, key: string, what: string): T {
  const value = map.get(key);
  if (value === undefined) throw new Error(`${what}: unknown key "${key}"`);
  return value;
}

export function indexBy<T>(items: readonly T[], key: (item: T) => string): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) map.set(key(item), item);
  return map;
}

export function groupBy<T>(items: readonly T[], key: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = map.get(k);
    if (bucket) bucket.push(item);
    else map.set(k, [item]);
  }
  return map;
}

export function unique<T>(items: readonly T[]): T[] {
  return Array.from(new Set(items));
}

export function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

/** Title-cased slug used for supplier and system display codes. */
export function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
